import React, { useState, useEffect, useCallback, useMemo } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import PageContainer from "../layouts/PageContainer";
import CardGridContainer from "../layouts/CardGridContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";

import SmsTemplateCard from "../components/sms/SmsTemplateCard";
import CreateSmsTemplateModal from "../components/sms/CreateSmsTemplateModal";
import UpdateSmsTemplateModal from "../components/sms/UpdateSmsTemplateModal";

import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getSmsTemplates,
  deleteSmsTemplate,
  resetSmsTemplateServerStatus,
} from "../redux/actions/smsTemplateActions";
import { SmsTemplateResponseDto } from "../shared/dtos/sms/SmsTemplateResponseDto";

const SmsTemplatesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    items: smsTemplates,
    loadingFetchAll: loadingSmsTemplates,
    errorFetchAll: errorLoadingSmsTemplates,
    serverStatus,
    loadingDelete: loadingSmsTemplateDelete,
    loadingCreate: loadingSmsTemplateCreate,
    loadingUpdate: loadingSmsTemplateUpdate,
    errorCreate: errorCreatingSmsTemplate,
    errorUpdate: errorUpdatingSmsTemplate,
  } = useAppSelector((state) => state.smsTemplate);

  // Local Search State
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<SmsTemplateResponseDto | null>(null);

  const canCreate = hasPermission("sms_template:create");
  const canUpdate = hasPermission("sms_template:update");
  const canDelete = hasPermission("sms_template:delete");

  const fetchTemplates = useCallback((): void => {
    dispatch(getSmsTemplates());
  }, [dispatch]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Frontend Filter
  const filteredTemplates = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return smsTemplates;

    return smsTemplates.filter((template) => {
      const matchesCode = template.templateCode?.toLowerCase().includes(query);
      const matchesBody = (template as any).messageBody
        ?.toLowerCase()
        .includes(query);
      const matchesDescription = (template as any).description
        ?.toLowerCase()
        .includes(query);

      return Boolean(matchesCode || matchesBody || matchesDescription);
    });
  }, [smsTemplates, searchTerm]);

  // Modal Handlers
  const handleCloseCreate = useCallback(
    () => !loadingSmsTemplateCreate && setIsCreateModalOpen(false),
    [loadingSmsTemplateCreate],
  );
  const handleCloseUpdate = useCallback(
    () =>
      !loadingSmsTemplateUpdate &&
      (setIsUpdateModalOpen(false), setSelectedTemplate(null)),
    [loadingSmsTemplateUpdate],
  );
  const handleCloseDelete = useCallback(
    () =>
      !loadingSmsTemplateDelete &&
      (setIsDeleteModalOpen(false), setSelectedTemplate(null)),
    [loadingSmsTemplateDelete],
  );

  useEffect(() => {
    if (!serverStatus) return;
    if (serverStatus.operation === "create" && serverStatus.status === 201)
      handleCloseCreate();
    if (serverStatus.operation === "update" && serverStatus.status === 200)
      handleCloseUpdate();
    if (serverStatus.operation === "delete" && serverStatus.status === 200)
      handleCloseDelete();

    dispatch(resetSmsTemplateServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  return (
    <PageContainer
      title="SMS Templates"
      subtitle="Manage standard SMS messages and dynamic communication templates."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setIsCreateModalOpen(true)}
          >
            Add Template
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search template code or message content..."
      />

      <CardGridContainer
        loading={loadingSmsTemplates}
        error={errorLoadingSmsTemplates}
        itemCount={filteredTemplates.length}
        emptyMessage="No SMS Templates match your search"
        onRetry={fetchTemplates}
      >
        {filteredTemplates.map((template) => (
          <SmsTemplateCard
            key={template.id}
            template={template}
            onEdit={
              canUpdate
                ? () => {
                    setSelectedTemplate(template);
                    setIsUpdateModalOpen(true);
                  }
                : undefined
            }
            onDelete={
              canDelete
                ? () => {
                    setSelectedTemplate(template);
                    setIsDeleteModalOpen(true);
                  }
                : undefined
            }
          />
        ))}
      </CardGridContainer>

      {/* CREATE MODAL */}
      <CreateSmsTemplateModal
        open={isCreateModalOpen}
        onClose={handleCloseCreate}
        loading={loadingSmsTemplateCreate}
        error={errorCreatingSmsTemplate}
      />

      {/* UPDATE MODAL */}
      {selectedTemplate && (
        <UpdateSmsTemplateModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          template={selectedTemplate}
          loading={loadingSmsTemplateUpdate}
          error={errorUpdatingSmsTemplate}
        />
      )}

      {/* DELETE DIALOG */}
      {selectedTemplate && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={() => dispatch(deleteSmsTemplate(selectedTemplate.id))}
          title="Delete Template"
          description={`Are you sure you want to delete template "${selectedTemplate.templateCode}"? This action cannot be undone.`}
          loading={loadingSmsTemplateDelete}
        />
      )}
    </PageContainer>
  );
};

export default SmsTemplatesPage;
